/**
 * Returns the current SIP connection status variant for UI (available, reconnecting, unavailable).
 * Uses useConnectionCoordinator and useRecoveryCoordinator internally so callers get a single
 * typed value and avoid unsafe assignment from destructured hook results.
 */

import type { SipStatusVariant } from './index.types';
import { useConnectionCoordinator } from '../useConnectionCoordinator';
import { useRecoveryCoordinator } from '../useRecoveryCoordinator';

export type { SipStatusVariant } from './index.types';

function resolveStatusVariant(
  isWebRTCConnected: boolean,
  isWebRTCReconnecting: boolean,
  webRTCRegistrationConcluded: boolean,
  isReconnecting: boolean
): SipStatusVariant {
  if (isReconnecting || isWebRTCReconnecting) {
    return 'reconnecting';
  }

  if (
    isWebRTCConnected &&
    webRTCRegistrationConcluded &&
    !isWebRTCReconnecting
  ) {
    return 'available';
  }

  return 'unavailable';
}

export function useSipStatusVariant(): SipStatusVariant {
  const {
    isWebRTCConnected,
    isWebRTCReconnecting,
    webRTCRegistrationConcluded,
  } = useConnectionCoordinator();
  const { isReconnecting } = useRecoveryCoordinator();

  return resolveStatusVariant(
    isWebRTCConnected,
    isWebRTCReconnecting,
    webRTCRegistrationConcluded,
    isReconnecting
  );
}
